import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { StorageService } from '@morningstar/shared/base/storage-service';
import { MeetingAttendee, MeetingAttendeesComponent } from '@morningstar/shared/meeting-attendees';
import { confirm, select } from '@morningstar/shared/operators';
import { combineLatest, merge, Subject } from 'rxjs';
import { filter, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { DashboardBaseComponent } from '../../dashboard-base/dashboard-base.component';
import { MeetingRoomService } from '../meeting-room.service';
import { MeetingRoomSelectors } from './../meeting-room.selectors';
import { MeetingAttendance } from './meeting-attendance';

@Component({
  selector: 'wsg-meeting-arrivals',
  templateUrl: './meeting-arrivals.component.html',
  styleUrls: ['./meeting-arrivals.component.scss', '../../dashboard.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MeetingArrivalsComponent extends DashboardBaseComponent implements OnDestroy {

  // == CONSTANTS =======================================================

  title = 'Meeting Arrivals';

  // == STATE ===========================================================

  form = this.formBuilder.group({
    location: ''
  });

  state$ = this.meetingRoomService.state$;
  processing$ = this.meetingRoomService.processingArrival$;

  // == ACTIONS =========================================================

  getAttendance = new Subject<void>();
  getAttendance$ = this.getAttendance.asObservable();

  markArrived = new Subject<MeetingAttendee>();
  markArrived$ = this.markArrived.asObservable();

  manageAttendance = new Subject<number>();
  manageAttendance$ = this.manageAttendance.asObservable();

  // == INTERMEDIATE ====================================================

  attendance$ = this.state$.pipe(
    select<MeetingAttendance[]>(MeetingRoomSelectors.attendance)
  );

  location$ = this.location.valueChanges;

  // == SIDE EFFECTS ====================================================

  onGetAttendance$ = combineLatest([this.getAttendance$, this.location$]).pipe(
    filter(([_, location]) => !!location),
    tap(([_, location]) => this.meetingRoomService.getAttendance(location))
  );

  onMarkArrived$ = this.markArrived$.pipe(
    confirm(_ => "Mark arrived and send notifications?", this.dialog, true),
    withLatestFrom(this.markArrived$, this.location$),
    tap(([_, attendee, location]) => this.meetingRoomService.markArrived(attendee, location))
  );

  onManageAttendance$ = this.manageAttendance$.pipe(
    mergeMap(meetingId => this.dialog.open(MeetingAttendeesComponent, { data: meetingId }).afterClosed()),
    tap(_ => this.getAttendance.next())
  );

  // == SUBSCRIPTION ====================================================

  subscription = merge(
    this.onGetAttendance$,
    this.onMarkArrived$,
    this.onManageAttendance$
  ).subscribe();

  // == LIFECYCLE =======================================================

  constructor(private meetingRoomService: MeetingRoomService,
              private formBuilder: FormBuilder,
              private dialog: MatDialog,
              protected storageService: StorageService) {
    super(storageService);

    this.getAttendance.next();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  // == HELPERS =========================================================

  get location(): AbstractControl {
    return this.form.get('location');
  }
}
